import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box, 
  Typography, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Button, 
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
// Removed Redux auth selectors - now using useAuth() hook
import { useAuth } from '../../context/AuthContext';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import { EncounterType } from '../../types/enums';
import { fetchFacilityById } from '../../redux/thunks/facilitiesThunks';

const EncounterTypeSelector = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userFacilities = useSelector(selectUserFacilities);
  const [selectedEncounterType, setSelectedEncounterType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [facility, setFacility] = useState(null);

  const navigateToEncounterForm = useCallback((encounterType) => {
    const routeMap = {
      [EncounterType.SURGERY]: `/patients/${patientId}/encounters/surgical/new`,
      [EncounterType.INPATIENT_ADMISSION]: `/patients/${patientId}/encounters/inpatient/new`,
      [EncounterType.OUTPATIENT_VISIT]: `/patients/${patientId}/encounters/outpatient/new`,
      [EncounterType.EMERGENCY_VISIT]: `/patients/${patientId}/encounters/emergency/new`,
      [EncounterType.TELEHEALTH]: `/patients/${patientId}/encounters/telehealth/new`,
      [EncounterType.OBSERVATION]: `/patients/${patientId}/encounters/observation/new`,
    };

    const route = routeMap[encounterType] || `/patients/${patientId}/encounters/new`;
    navigate(route, { 
      state: { 
        encounterType,
        isInpatient: encounterType === EncounterType.INPATIENT_ADMISSION 
      } 
    });
  }, [patientId, navigate]);

  useEffect(() => {
    const determineEncounterType = async () => {
      if (!user?.defaultFacility) {
        setError('No default facility set for user');
        setLoading(false);
        return;
      }

      // Try to find facility in userFacilities first
      let foundFacility = userFacilities.find(f => f.id === user.defaultFacility);
      // If not found, fetch directly by ID
      if (!foundFacility) {
        try {
          const result = await dispatch(fetchFacilityById(user.defaultFacility));
          if (result?.payload) {
            foundFacility = result.payload;
          }
        } catch {
          setError('Facility not found');
          setLoading(false);
          return;
        }
      }
      if (!foundFacility) {
        setError('Facility not found');
        setLoading(false);
        return;
      }
      setFacility(foundFacility);
      if (!Array.isArray(foundFacility.encounterTypes) || foundFacility.encounterTypes.length === 0) {
        setError('No encounter types configured for this facility');
        setLoading(false);
        return;
      }
      // If facility supports only one encounter type, auto-select it
      if (foundFacility.encounterTypes.length === 1) {
        const singleType = foundFacility.encounterTypes[0];
        setSelectedEncounterType(singleType);
        navigateToEncounterForm(singleType);
        return;
      }
      // If multiple types, default to first but allow selection
      setSelectedEncounterType(foundFacility.encounterTypes[0]);
      setLoading(false);
    };
    determineEncounterType();
     
  }, [user, userFacilities, navigate, dispatch, navigateToEncounterForm]);

  const handleEncounterTypeChange = (event) => {
    setSelectedEncounterType(event.target.value);
  };

  const handleContinue = () => {
    if (selectedEncounterType) {
      navigateToEncounterForm(selectedEncounterType);
    }
  };

  const getEncounterTypeLabel = (type) => {
    const labelMap = {
      [EncounterType.OUTPATIENT_VISIT]: 'Outpatient',
      [EncounterType.EMERGENCY_VISIT]: 'Emergency Visit',
      [EncounterType.INPATIENT_ADMISSION]: 'Inpatient',
      [EncounterType.TELEHEALTH]: 'Telehealth',
      [EncounterType.OBSERVATION]: 'Observation',
      [EncounterType.SURGERY]: 'Surgery',
      'homeHealth': 'Home Health',
    };
    return labelMap[type] || type;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Determining encounter type...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  const hasMultipleTypes = facility?.encounterTypes?.length > 1;

  if (!hasMultipleTypes) {
    return null; // Should have already navigated
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Select Encounter Type
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          This facility supports multiple encounter types. Please select the appropriate type for this encounter.
        </Typography>

        <RadioGroup
          value={selectedEncounterType}
          onChange={handleEncounterTypeChange}
          sx={{ mb: 3 }}
        >
          {/* Deduplicate encounter types to prevent duplicates from showing */}
          {[...new Set(facility.encounterTypes)].map(type => (
            <FormControlLabel
              key={type}
              value={type}
              control={<Radio />}
              label={getEncounterTypeLabel(type)}
              sx={{ mb: 1 }}
            />
          ))}
        </RadioGroup>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleContinue}
            disabled={!selectedEncounterType}
          >
            Continue
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EncounterTypeSelector; 